import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from './stores/authStore';
import { useRuntimeConfigStore } from './stores/runtimeConfigStore';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { WeChatCallbackPage } from './pages/WeChatCallbackPage';
import { GoogleCallbackPage } from './pages/GoogleCallbackPage';
import { TeamInvitePage } from './pages/TeamInvitePage';
import { WorkflowEditorPage } from './pages/WorkflowEditorPage';
import { WorkflowManagementPage } from './pages/WorkflowManagementPage';
import WorkflowVersionDiffPage from './pages/WorkflowVersionDiffPage';
import { RuleManagementPage } from './pages/RuleManagementPage';
import { DataManagementPage } from './pages/DataManagementPage';
import { SettingsPage } from './pages/SettingsPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { BillingPage } from './pages/subscription/BillingPage';
import { UsagePage } from './pages/subscription/UsagePage';
import { TeamListPage } from './pages/TeamListPage';
import { TeamDetailPage } from './pages/TeamDetailPage';
import { LicensePage } from './pages/LicensePage';
import { UpgradePage } from './pages/UpgradePage';
import { FeedbackPage } from './pages/FeedbackPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { TemplateListPage } from './pages/TemplateListPage';
import { TemplateApplyPage } from './pages/TemplateApplyPage';
import { DeveloperPage } from './pages/DeveloperPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { SharePage } from './pages/SharePage';
import { MainLayout } from './components/layout/MainLayout';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function RedirectWorkflowEditToStore() {
  const { workflowId } = useParams<{ workflowId: string }>();
  return <Navigate to={workflowId ? `/workflows/store/edit/${workflowId}` : '/workflows'} replace />;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/wechat-callback" element={<WeChatCallbackPage />} />
      <Route path="/google/auth" element={<GoogleCallbackPage />} />
      <Route path="/invite" element={<TeamInvitePage />} />
      <Route path="/invite/:token" element={<TeamInvitePage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/home" replace />} />
        <Route path="home" element={<Navigate to="/home/store" replace />} />
        <Route path="home/store" element={<WorkflowEditorPage mode="home-store" />} />
        <Route path="workflows" element={<WorkflowManagementPage />} />
        <Route path="workflows/edit/:workflowId" element={<RedirectWorkflowEditToStore />} />
        <Route path="workflows/store/edit/:workflowId" element={<WorkflowEditorPage mode="workflow-store" />} />
        <Route path="workflows/:workflowId/diff" element={<WorkflowVersionDiffPage />} />
        <Route path="rules" element={<RuleManagementPage />} />
        <Route path="data" element={<DataManagementPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="subscription/billing" element={<BillingPage />} />
        <Route path="subscription/usage" element={<UsagePage />} />
        <Route path="team" element={<TeamListPage />} />
        <Route path="team/:teamId" element={<TeamDetailPage />} />
        <Route path="upgrade" element={<UpgradePage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="license" element={<LicensePage />} />
        <Route path="templates" element={<TemplateListPage />} />
        <Route path="templates/:templateId/apply" element={<TemplateApplyPage />} />
        <Route path="developer" element={<DeveloperPage />} />
      </Route>
    </>
  )
);

function App() {
  // 初始化运行时配置
  const { fetchConfig } = useRuntimeConfigStore();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    console.log('app region', import.meta.env.VITE_APP_REGION);
  }, []);
  
  // 在应用启动时获取运行时配置
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // 动态设置页面 title，根据当前语言切换
  useEffect(() => {
    const appName = t('app.name');
    document.title = `${appName} - ${t('app.tagline', '智能 Excel 数据处理平台')}`;
  }, [t, i18n.language]);

  return <RouterProvider router={router} />;
}

export default App;
